import { Component, OnInit, Input } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Observable } from 'rxjs';
import { PartnerService } from 'src/app/_services/partner.service';
import { RentalPartnerService } from 'src/app/_services/rental/rental-partner.service';
import { YachtPartnerService } from 'src/app/_services/yacht/yacht-partner.service';
import { ToastrService } from 'ngx-toastr';

/**
 * Shared modal to update a partner's SPOC email address.
 * Used by car, yacht and rental partner detail pages.
 *
 * fromParent: {
 *   module: 'partner' | 'yachtPartner' | 'rentalPartner',
 *   partnerId: string,
 *   email: string,
 * }
 */
@Component({
  selector: 'app-spoc-email-update-modal',
  templateUrl: './spoc-email-update-modal.component.html',
  styleUrls: ['./spoc-email-update-modal.component.scss']
})
export class SpocEmailUpdateModalComponent implements OnInit {
  @Input() fromParent: any;
  spocEmailForm: FormGroup;
  loading: boolean = false;
  partnerId: string = '';
  initialEmail: string = '';
  readonly emailMaxLength = 30;

  constructor(
    private formBuilder: FormBuilder,
    public modal: NgbModal,
    private toastrService: ToastrService,
    private partnerService: PartnerService,
    private yachtPartnerService: YachtPartnerService,
    private rentalPartnerService: RentalPartnerService,
  ) {}

  ngOnInit(): void {
    this.initializeForm();
    this.prefillForm();
  }

  initializeForm() {
    this.spocEmailForm = this.formBuilder.group({
      email: ['', [
        Validators.required,
        Validators.maxLength(this.emailMaxLength),
        Validators.email,
        Validators.pattern('^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\\.[A-Za-z]{2,}$')
      ]],
    });
  }

  private prefillForm() {
    if (!this.fromParent) {
      return;
    }

    this.partnerId = this.fromParent.partnerId || '';
    const email = this.fromParent.email || '';

    this.spocEmailForm.patchValue({ email });
    this.initialEmail = email;
  }

  private getUpdateRequest(body: { email: string }): Observable<any> | null {
    switch (this.fromParent?.module) {
      case 'partner':
        return this.partnerService.updateSpocEmail(this.partnerId, body);
      case 'yachtPartner':
        return this.yachtPartnerService.updateSpocEmail(this.partnerId, body);
      case 'rentalPartner':
        return this.rentalPartnerService.updateSpocEmail(this.partnerId, body);
      default:
        return null;
    }
  }

  private refreshPartner() {
    switch (this.fromParent?.module) {
      case 'partner':
        this.partnerService.setRefreshPartner(true);
        break;
      case 'yachtPartner':
        this.yachtPartnerService.setRefreshPartner(true);
        break;
      case 'rentalPartner':
        this.rentalPartnerService.setRefreshPartner(true);
        break;
    }
  }

  spocEmailSubmit(value: any) {
    if (!this.partnerId) {
      this.toastrService.error('Partner ID is missing', 'Error');
      return;
    }

    if (this.spocEmailForm.invalid) {
      this.spocEmailForm.markAllAsTouched();
      return;
    }

    const email = (value.email || '').trim().toLowerCase();

    if (email === this.initialEmail.trim().toLowerCase()) {
      this.toastrService.info('No changes detected', 'Info');
      return;
    }

    const request = this.getUpdateRequest({ email });
    if (!request) {
      this.toastrService.error('Unknown partner type', 'Error');
      return;
    }

    this.loading = true;

    request.subscribe(
      (response: any) => {
        this.loading = false;
        if (response === true || response?.statusCode === 200 || response?.code === 200 || response?.data === true) {
          this.toastrService.success('SPOC email address updated successfully', 'Success');
          this.refreshPartner();
          this.modal.dismissAll();
        } else {
          this.toastrService.error(response?.message || 'Failed to update SPOC email address', 'Error');
        }
      },
      (error: any) => {
        this.loading = false;
        this.toastrService.error(this.extractApiError(error), 'Error');
      }
    );
  }

  private extractApiError(error: any): string {
    const body = error?.error;
    const message = body?.error || body?.message || body?.CustomBadRequestError;

    if (typeof message === 'string' && message !== 'BAD_REQUEST') {
      return message;
    }
    return error?.message || 'An error occurred while updating SPOC email address';
  }

  hasFormChanged(): boolean {
    const currentEmail = this.spocEmailForm.get('email')?.value || '';
    return currentEmail.trim().toLowerCase() !== this.initialEmail.trim().toLowerCase();
  }
}
